import type { Audience, Viewer } from '../audience/audience';
import { isInAudience } from '../audience/audience';
import { DAY_MS } from '../days';
import type { ExperienceState } from '../state/experienceState';

import type { Campaign } from './campaignTypes';

// Pure decision rules for campaigns, pinned by `campaignPolicy.node-test.ts`.

export const DEFAULT_MAX_DISMISSALS = 2;

export type SkipReason =
  | 'inactive'
  | 'audience'
  | 'impersonating'
  | 'session_budget'
  | 'done'
  | 'cooldown'
  | 'dismissed_too_often'
  | 'not_eligible';

export type CampaignDecision =
  | { action: 'show' }
  | { action: 'holdout' }
  | { action: 'skip'; reason: SkipReason };

export type PrecheckInput = {
  id: string;
  campaign: Pick<
    Campaign,
    | 'tour'
    | 'repeatAfterDays'
    | 'maxDismissals'
    | 'active'
    | 'accountsCreatedBy'
  > & { audience: Audience };
  state: ExperienceState;
  viewer: Viewer;
  now: Date;
  today: string;
  autoTourShownThisSession: boolean;
  isImpersonating: boolean;
};

// Everything that can be decided without a query. `null` = go on to the
// campaign's own `isEligible` check.
export const precheckCampaign = ({
  id,
  campaign,
  state,
  viewer,
  now,
  today,
  autoTourShownThisSession,
  isImpersonating,
}: PrecheckInput): CampaignDecision | null => {
  const { active } = campaign;
  if (
    (active?.from != null && today < active.from) ||
    (active?.until != null && today > active.until)
  ) {
    return { action: 'skip', reason: 'inactive' };
  }
  if (
    !isInAudience(campaign.audience, viewer) ||
    (campaign.accountsCreatedBy != null &&
      viewer.accountCreatedDay != null &&
      viewer.accountCreatedDay > campaign.accountsCreatedBy)
  ) {
    return { action: 'skip', reason: 'audience' };
  }
  if (isImpersonating) {
    return { action: 'skip', reason: 'impersonating' };
  }

  const record = state.campaigns[id];
  if (record?.holdout === true) {
    // Sticky: a holdout user stays out for the campaign's whole life.
    return { action: 'holdout' };
  }
  const maxDismissals = campaign.maxDismissals ?? DEFAULT_MAX_DISMISSALS;
  if ((record?.dismissCount ?? 0) >= maxDismissals) {
    return { action: 'skip', reason: 'dismissed_too_often' };
  }
  if (record != null && campaign.repeatAfterDays == null) {
    return { action: 'skip', reason: 'done' };
  }

  // Last time the user went through this campaign, or through the tour by
  // any other way ("Show me", another campaign).
  const tour = state.tours[campaign.tour];
  const finishedAt = [record, tour]
    .filter(entry => entry?.outcome != null)
    .map(entry => Date.parse(entry?.at ?? ''))
    .filter(time => !Number.isNaN(time));
  if (finishedAt.length > 0) {
    if (campaign.repeatAfterDays == null) {
      return { action: 'skip', reason: 'done' };
    }
    const last = Math.max(...finishedAt);
    if (now.getTime() - last < campaign.repeatAfterDays * DAY_MS) {
      return { action: 'skip', reason: 'cooldown' };
    }
  }

  if (autoTourShownThisSession) {
    return { action: 'skip', reason: 'session_budget' };
  }
  return null;
};

// FNV-1a: a stable bucket in [0, 1) per user and campaign, so the holdout
// split needs no storage and no flag service, and survives a reset.
export const holdoutBucket = (userId: string, campaignId: string): number => {
  let hash = 0x811c9dc5;
  for (const char of `${campaignId}:${userId}`) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash / 0x100000000;
};

export const decideAfterEligibility = ({
  eligible,
  holdout,
  bucket,
}: {
  eligible: boolean;
  holdout: number | undefined;
  bucket: number;
}): CampaignDecision => {
  if (!eligible) {
    return { action: 'skip', reason: 'not_eligible' };
  }
  return bucket < (holdout ?? 0) ? { action: 'holdout' } : { action: 'show' };
};
